import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
// Компоненты
import Table from '../Table/Table';
import CustomButton from '../CustomElements/CustomButton';
import CustomButtonEdit from '../CustomElements/CustomButtonEdit';
import CustomButtonDelete from '../CustomElements/CustomButtonDelete';
import ButtonsPanelInBlock from './ButtonsPanelInBlock';
// Утилиты
import { defaultCellRender } from '../../utils/formatValueForHTML';
import { useRole } from '../../customHook/useRole';

/**
 * Компонент таблицы для отображения данных административной панели
 *
 * @param {string} entityKey - ключ сущности (например, 'roads', 'employees')
 * @param {Object} entityConfig - конфигурация сущности из entitiesConfig
 * @param {Function} onAddNew - обработчик кнопки добавления новой записи
 * @param {Function} onEdit - обработчик редактирования строки
 * @param {Function} onDelete - обработчик удаления строки (только для emails)
 */
function EntityTable({ entityKey, entityConfig, onAddNew, onEdit, onDelete, t }) {
  // определяем что мы на странице рассылок  чтобы отрисовать доп флаг
  const isEmails = entityKey === 'mailings';
  // флаги для отображения кнопок редактирования и удаления
  const showEditButton = entityConfig?.showEditButton;
  const showDeleteButton = entityConfig?.showDeleteButton;
  // состояние для удаляемой рассылки
  const [selectedMailingId, setSelectedMailingId] = useState(null);
  // определение роли
  const isMaster = useRole('master');
  const isAdmin = useRole('admin');
  const roleAccess = {
    admin: isAdmin,
    master: isMaster,
    // и т.д. если нужно
  };
  // по умолчанию: все, кроме мастера
  const allowedDeleteRoles = entityConfig?.allowedRolesForDelete;
  const canDelete = allowedDeleteRoles ? allowedDeleteRoles.some((role) => roleAccess[role]) : !isMaster;

  // Получаем данные из Redux по ключу сущности
  const backendData = useSelector((state) => state.adminData);
  const entityData = useMemo(() => {
    // Если для данной сущности предусмотрена своя логика получения данных для таблицы
    if (typeof entityConfig?.getTableData === 'function') {
      return entityConfig.getTableData(backendData, {
        entityKey, // имя сущности (например, 'mailings')
        selectedId: selectedMailingId, // выбранный ID (например, id рассылки)
      });
    }
    // По умолчанию, если нет специальной функции, предполагаем что данные — это массив:
    // backendData[entityKey] — массив объектов (например, список дорог, сотрудников и т.п.)
    return backendData?.[entityKey] || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendData, entityKey, selectedMailingId]);

  const columns = useMemo(() => {
    if (!entityConfig?.columns) return [];

    /**
     * Возвращает компонент ячейки для конкретного поля.
     * Использует кастомный рендер из entityConfig.customRender, если он есть.
     * В противном случае — дефолтный рендер.
     */
    const renderCell =
      (accessor) =>
      ({ value }) =>
        entityConfig.customRender?.[accessor] ? entityConfig.customRender[accessor](value) : defaultCellRender(value);

    /**
     * Рекурсивная функция для обработки массива колонок, включая вложенные.
     * Для каждой колонки:
     * - если есть вложенные колонки (subCols), вызывается рекурсивно;
     * - если это "обычная" колонка с accessor — добавляется Cell для рендера.
     */
    const processColumns = (cols) =>
      cols.map(({ Header, accessor, disableSorting, columns: subCols, ...rest }) => {
        const base = {
          Header, // Заголовок колонки
          disableSorting, // Отключение сортировки
          ...rest, // Остальные поля из конфига
        };

        // Если колонка содержит вложенные колонки — обрабатываем их рекурсивно
        if (subCols) {
          return {
            ...base,
            columns: processColumns(subCols),
          };
        }

        // Если колонка обычная — добавляем accessor и Cell
        return {
          ...base,
          accessor,
          Cell: renderCell(accessor),
        };
      });

    // Основной массив колонок, обработанный с поддержкой вложенности
    const baseColumns = processColumns(entityConfig.columns);

    // Добавляем колонку "Редактировать", если это разрешено (и не мастер-пользователь)
    if (!isMaster && showEditButton) {
      baseColumns.push({
        Header: t?.editButtonText, // Заголовок кнопки
        disableSorting: true,
        Cell: ({ row: { original } }) => <CustomButtonEdit onClick={() => onEdit(original)} />,
      });
    }

    // Добавляем колонку "Удалить", если разрешено
    if (canDelete && showDeleteButton) {
      baseColumns.push({
        Header: t?.deleteButton,
        disableSorting: true,
        Cell: ({ row: { original } }) => (
          <CustomButtonDelete
            onClick={() => {
              onDelete(original);
            }}
          />
        ),
      });
    }

    // Возвращаем итоговый массив колонок
    return baseColumns;
  }, [entityConfig, isMaster, onEdit, onDelete, showDeleteButton, showEditButton, t, canDelete]);

  return (
    <section className="adding-data-block">
      <div className="adding-data-block__content">
        {/* Дополнительная панель кнопок для "emails" */}
        {isEmails && <ButtonsPanelInBlock setSelectedMailingId={setSelectedMailingId} t={t} />}

        <div className="adding-data-block__table">
          {/* Таблица с данными и хедером с поиском */}
          <Table
            className="adding-data-block__inner-table"
            data={entityData || []}
            columns={columns}
            showGlobalSearch={true}
          />

          {/* Кнопка добавления новой записи */}
          {!isMaster && !entityConfig?.hideAddButton && (
            <div className={`adding-data-block__button ${isMaster ? 'padding-10' : ''}`}>
              <CustomButton
                text={`+ ${entityConfig.addLabel}`}
                onClick={() => onAddNew({ keyName: selectedMailingId })}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default EntityTable;
